import { Dropdown, DropdownProps, MenuProps, Tooltip, TooltipProps } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { CSSProperties, useEffect, useState } from 'react'
import style from './ModelPopover.module.less'

import { userService } from '@/services/userService/userService'
import { UpgradeLinkBtn } from '../UpgradeLinkBtn/UpgradeLinkBtn'
import { VipBadge } from '../VipBadge/VipBadge'

export interface ModelPopoverProps extends DropdownProps {
  currentModel: ServerDataTypes.Model
  onModelChange: (model: ServerDataTypes.Model) => void
  className?: string
  customStyles?: CSSProperties
  onRenderCurrentItem: (item: ServerDataTypes.Model) => React.ReactNode
  disableTooltip?: boolean
  tooltipPlacement?: TooltipProps['placement']
  tooltipOverlayStyle?: CSSProperties
  apiFilter: ServerDataTypes.ModelFilter
  /** 是否延迟加载模型列表，为true时只有在打开Popover时才会调用接口 */
  lazy?: boolean
  modelList?: ServerDataTypes.Model[]
  optionList?: string[] // 可选的列表
  excludeModelList?: string[] // 排除的列表
}

export const ModelPopover = observer((props: ModelPopoverProps) => {
  const {
    currentModel,
    onModelChange,
    customStyles,
    className,
    disableTooltip = true,
    tooltipPlacement = 'right',
    tooltipOverlayStyle = { maxWidth: '300px' },
    lazy = false,
    apiFilter,
    modelList,
    optionList,
    excludeModelList,
    ...restProps
  } = props
  const isUltra = userService.vipVersion === 'ultra'
  const isFree = userService.vipVersion === 'free'

  const [freeModelList, setFreeModelList] = useState<ServerDataTypes.Model[]>([])
  const [premiumModelList, setPremiumModelList] = useState<ServerDataTypes.Model[]>([])
  const [ultraModelList, setUltraModelList] = useState<ServerDataTypes.Model[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const fetchModelList = async () => {
    if (hasLoaded) return // 避免重复加载

    let dataList: ServerDataTypes.Model[] = []
    if (optionList && optionList.length) {
      dataList = modelList || []
      dataList = dataList.filter((item) => optionList.includes(item.key))
      //console.log('dataList-MODEL-LIST-POPUP',dataList)
    } else {
      const newDataList = await userService.getLLMModelList(apiFilter!)
      if (excludeModelList && excludeModelList.length) {
        dataList = newDataList.filter((item) => !excludeModelList.includes(item.key))
      } else {
        dataList = newDataList
      }
    }

    const freeModelList = dataList.filter((item) => item.level === 1)
    const premiumModelList = dataList.filter((item) => item.level === 2)
    const ultraModelList = dataList.filter((item) => item.level === 3)

    setFreeModelList(freeModelList)
    setPremiumModelList(premiumModelList)
    setUltraModelList(ultraModelList)
    setHasLoaded(true)
  }

  useEffect(() => {
    // 只有在非延迟模式下才立即加载
    if (!lazy) {
      fetchModelList()
    }
  }, [lazy])

  const menuItems: MenuProps['items'] = [
    {
      key: 'free',
      type: 'group',
      label: '基础模型（免费使用）',
      children: freeModelList.map((model) => ({
        key: model.key,
        label: (
          <Tooltip
            title={disableTooltip ? '' : model.desc}
            placement={tooltipPlacement}
            overlayStyle={tooltipOverlayStyle}
          >
            <div
              className={classNames('model-item', {
                'model-item-active': model.key === currentModel.key,
              })}
            >
              <div className='model-info'>
                <img src={model.icon} alt={model.name} className='model-logo' />
                <span className='model-label'>{model.name}</span>
              </div>
            </div>
          </Tooltip>
        ),
        onClick: () => {
          onModelChange(model)
        },
      })),
    },
    {
      key: 'premium',
      label: (
        <div className={classNames('group-title')}>
          高级模型
          {isFree && (
            <UpgradeLinkBtn
              version='max'
              text='畅享高级模型'
              source='modelPopover'
              feature='other'
            />
          )}
        </div>
      ),
      type: 'group',
      children: premiumModelList.map((model) => ({
        key: model.key,
        label: (
          <Tooltip
            title={disableTooltip ? '' : model.desc}
            placement={tooltipPlacement}
            overlayStyle={tooltipOverlayStyle}
          >
            <div
              className={classNames('model-item', {
                'model-item-active': model.key === currentModel.key,
              })}
            >
              <div className='model-info'>
                <img src={model.icon} alt={model.name} className='model-logo' />
                <span className='model-label'>{model.name}</span>
              </div>
              <VipBadge version='max' />
            </div>
          </Tooltip>
        ),
        onClick: () => {
          onModelChange(model)
        },
      })),
    },
    // 只有当ultraModelList长度大于0时才显示顶级模型部分
    ...(ultraModelList.length > 0
      ? [
          {
            key: 'ultra',
            label: (
              <div className={classNames('group-title')}>
                顶级模型
                {!isUltra && (
                  <UpgradeLinkBtn
                    version='ultra'
                    text='解锁顶级模型'
                    source='modelPopover'
                    feature='other'
                  />
                )}
              </div>
            ),
            type: 'group' as const,
            children: ultraModelList.map((model) => ({
              key: model.key,
              label: (
                <Tooltip
                  title={disableTooltip ? (isUltra ? '' : '仅Ultra会员可选') : model.desc}
                  placement={tooltipPlacement}
                  overlayStyle={tooltipOverlayStyle}
                >
                  <div
                    className={classNames('model-item', {
                      'model-item-active': model.key === currentModel.key,
                      'model-item-disabled': !isUltra,
                    })}
                  >
                    <div className='model-info'>
                      <img src={model.icon} alt={model.name} className='model-logo' />
                      <span className='model-label'>{model.name}</span>
                    </div>
                    <VipBadge version='ultra' />
                  </div>
                </Tooltip>
              ),
              disabled: !isUltra,
              onClick: async () => {
                onModelChange(model)
              },
            })),
          },
        ]
      : []),
  ]

  return (
    <Dropdown
      menu={{ items: menuItems }}
      placement='bottom'
      trigger={['click']}
      getPopupContainer={(el) => el.parentElement!}
      dropdownRender={(menu) => {
        return <div className={classNames(style.modelPopover, className)}>{menu}</div>
      }}
      {...restProps}
    >
      {props.onRenderCurrentItem(currentModel)}
    </Dropdown>
  )
})
